// The unit catalogue (v10). One place says, for every unit a product can be counted in:
//   • its Bangla / English label,
//   • whether quantities must be whole (a strip, a box, a pair) or may be fractional (kg, litre, metre),
//   • which shop categories offer it (CATEGORY_UNITS) — a clothing shop never sees "kg", a grocery never sees "strip".
// The server accepts every unit in UNITS for every category (old data, imports, other devices); the lists only shape the forms.
import { UNITS, type Unit } from './enums';
import type { Category } from './enums';
import { bnToEnDigits } from './money';

export type UnitKind = 'count' | 'pack' | 'weight' | 'volume' | 'length' | 'area';

export interface UnitInfo {
  bn: string;
  en: string;
  kind: UnitKind;
  /** quantities in this unit are whole numbers only (5, 6 — never 5.5) */
  whole: boolean;
}

export const UNIT_INFO: Record<Unit, UnitInfo> = {
  pcs: { bn: 'পিস', en: 'pcs', kind: 'count', whole: true },
  kg: { bn: 'কেজি', en: 'kg', kind: 'weight', whole: false },
  g: { bn: 'গ্রাম', en: 'g', kind: 'weight', whole: false },
  l: { bn: 'লিটার', en: 'litre', kind: 'volume', whole: false },
  ml: { bn: 'মিলি', en: 'ml', kind: 'volume', whole: false },
  dozen: { bn: 'ডজন', en: 'dozen', kind: 'pack', whole: true },
  box: { bn: 'বক্স', en: 'box', kind: 'pack', whole: true },
  packet: { bn: 'প্যাকেট', en: 'packet', kind: 'pack', whole: true },
  ft: { bn: 'ফুট', en: 'ft', kind: 'length', whole: false },
  m: { bn: 'মিটার', en: 'metre', kind: 'length', whole: false },
  other: { bn: 'অন্যান্য', en: 'other', kind: 'count', whole: false },
  strip: { bn: 'পাতা (স্ট্রিপ)', en: 'strip', kind: 'pack', whole: true },
  tablet: { bn: 'ট্যাবলেট', en: 'tablet', kind: 'count', whole: true },
  capsule: { bn: 'ক্যাপসুল', en: 'capsule', kind: 'count', whole: true },
  bottle: { bn: 'বোতল', en: 'bottle', kind: 'pack', whole: true },
  vial: { bn: 'ভায়াল', en: 'vial', kind: 'pack', whole: true },
  ampoule: { bn: 'অ্যাম্পুল', en: 'ampoule', kind: 'pack', whole: true },
  tube: { bn: 'টিউব', en: 'tube', kind: 'pack', whole: true },
  sachet: { bn: 'স্যাশে', en: 'sachet', kind: 'pack', whole: true },
  pair: { bn: 'জোড়া', en: 'pair', kind: 'count', whole: true },
  set: { bn: 'সেট', en: 'set', kind: 'count', whole: true },
  yard: { bn: 'গজ', en: 'yard', kind: 'length', whole: false },
  inch: { bn: 'ইঞ্চি', en: 'inch', kind: 'length', whole: false },
  hali: { bn: 'হালি', en: 'hali (4)', kind: 'pack', whole: true },
  maund: { bn: 'মণ', en: 'maund', kind: 'weight', whole: false },
  carton: { bn: 'কার্টন', en: 'carton', kind: 'pack', whole: true },
  bag: { bn: 'বস্তা', en: 'bag', kind: 'pack', whole: true },
  can: { bn: 'কৌটা / ক্যান', en: 'can', kind: 'pack', whole: true },
  jar: { bn: 'জার', en: 'jar', kind: 'pack', whole: true },
  roll: { bn: 'রোল', en: 'roll', kind: 'pack', whole: true },
  sheet: { bn: 'শিট', en: 'sheet', kind: 'count', whole: true },
  ream: { bn: 'রিম', en: 'ream', kind: 'pack', whole: true },
  sqft: { bn: 'বর্গফুট', en: 'sq ft', kind: 'area', whole: false },
  cft: { bn: 'ঘনফুট', en: 'cu ft', kind: 'volume', whole: false },
  ton: { bn: 'টন', en: 'ton', kind: 'weight', whole: false },
};

const ALL: readonly Unit[] = UNITS;
/** Units each category offers on its forms (first = the default for a new product). `other` is always last. */
export const CATEGORY_UNITS: Record<Category, readonly Unit[]> = {
  pharmacy: ['pcs', 'tablet', 'capsule', 'strip', 'box', 'bottle', 'vial', 'ampoule', 'tube', 'sachet', 'packet', 'ml', 'g', 'other'],
  clothing: ['pcs', 'pair', 'set', 'dozen', 'yard', 'm', 'other'],
  grocery: ['kg', 'g', 'pcs', 'l', 'ml', 'packet', 'dozen', 'hali', 'bottle', 'can', 'jar', 'box', 'carton', 'bag', 'maund', 'other'],
  general: ['pcs', 'kg', 'g', 'l', 'ml', 'packet', 'dozen', 'hali', 'box', 'bottle', 'can', 'jar', 'carton', 'bag', 'other'],
  departmental: ['pcs', 'kg', 'g', 'l', 'ml', 'packet', 'dozen', 'hali', 'box', 'bottle', 'can', 'jar', 'set', 'pair', 'carton', 'other'],
  electronics: ['pcs', 'set', 'pair', 'box', 'packet', 'm', 'roll', 'other'],
  hardware: ['pcs', 'kg', 'ft', 'm', 'inch', 'sqft', 'cft', 'l', 'box', 'packet', 'set', 'pair', 'roll', 'sheet', 'bag', 'dozen', 'ton', 'other'],
  stationary: ['pcs', 'dozen', 'box', 'packet', 'set', 'ream', 'sheet', 'roll', 'other'],
  wholesale: ['pcs', 'kg', 'carton', 'box', 'packet', 'dozen', 'bag', 'maund', 'ton', 'l', 'bottle', 'can', 'other'],
  others: ALL,
  agriculture: ['kg', 'maund', 'ton', 'bag', 'l', 'pcs', 'other'],
  restaurant: ['pcs', 'set', 'kg', 'g', 'l', 'ml', 'bottle', 'other'],
  tailoring: ['pcs', 'set', 'pair', 'yard', 'm', 'inch', 'other'],
  mobile_banking: ['pcs', 'other'],
  real_estate: ['pcs', 'sqft', 'other'],
  software: ['pcs', 'set', 'other'],
  personal_savings: ['pcs', 'other'],
};

/** Units for a category's product form; the product's current unit is kept even when the list does not offer it (old data stays editable). */
export function unitsFor(category: Category, current?: string | null): Unit[] {
  const list = [...(CATEGORY_UNITS[category] ?? ALL)];
  if (current && (UNITS as readonly string[]).includes(current) && !list.includes(current as Unit)) list.unshift(current as Unit);
  return list;
}
export const defaultUnitFor = (category: Category): Unit => CATEGORY_UNITS[category]?.[0] ?? 'pcs';

export const isKnownUnit = (u: string | null | undefined): u is Unit => !!u && (UNITS as readonly string[]).includes(u);

/**
 * Must a quantity in this unit be a whole number? Known measurable units (kg, litre, metre…) allow fractions;
 * counted / packed units do not. A free-text conversion unit (product_units.unitName, e.g. "পাতা") is a pack → whole,
 * unless it is the name of a measurable unit.
 */
export function isWholeUnit(u: string | null | undefined): boolean {
  if (!u) return false;
  const key = u.trim().toLowerCase();
  if (isKnownUnit(key)) return UNIT_INFO[key].whole;
  const hit = (Object.entries(UNIT_INFO) as Array<[Unit, UnitInfo]>).find(([, i]) => i.bn === u.trim() || i.en.toLowerCase() === key);
  if (hit) return hit[1].whole;
  return true;
}
/** Decimal places a quantity input allows for this unit: 0 for whole units, 3 otherwise. */
export const qtyDecimalsFor = (u: string | null | undefined): number => (isWholeUnit(u) ? 0 : 3);
/** Typed text → a quantity string with at most `dp` decimals (Bangla digits allowed). With dp 0 the decimal point and what follows are dropped. */
export function cleanQtyInput(raw: string, dp: number): string {
  let v = bnToEnDigits(raw).replace(/[^0-9.]/g, '');
  const dot = v.indexOf('.');
  if (dot >= 0) v = dp > 0 ? v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, '').slice(0, dp) : v.slice(0, dot);
  return v;
}
/** Is `qty` acceptable for the unit (whole units → integer)? */
export function qtyFitsUnit(qty: string | number, u: string | null | undefined): boolean {
  const s = String(qty).trim();
  if (!s) return false;
  if (!isWholeUnit(u)) return true;
  return /^\d+(\.0+)?$/.test(s);
}

export function unitLabel(u: string | null | undefined, lang: 'bn' | 'en' = 'bn'): string {
  if (!u) return '';
  return isKnownUnit(u) ? UNIT_INFO[u][lang] : u;
}

/** Bangla / English names people type in imports → unit key. */
export function unitFromName(raw: string): Unit | null {
  const s = raw.trim();
  if (!s) return null;
  const low = s.toLowerCase();
  if (isKnownUnit(low)) return low;
  const alias: Record<string, Unit> = {
    'কেজি': 'kg', 'পিস': 'pcs', 'টি': 'pcs', 'লিটার': 'l', 'গ্রাম': 'g', 'ডজন': 'dozen', 'বক্স': 'box', 'প্যাকেট': 'packet', 'ফুট': 'ft', 'মিটার': 'm',
    'পাতা': 'strip', 'স্ট্রিপ': 'strip', 'ট্যাবলেট': 'tablet', 'ক্যাপসুল': 'capsule', 'বোতল': 'bottle', 'ভায়াল': 'vial', 'টিউব': 'tube', 'জোড়া': 'pair', 'সেট': 'set',
    'গজ': 'yard', 'ইঞ্চি': 'inch', 'হালি': 'hali', 'মণ': 'maund', 'কার্টন': 'carton', 'বস্তা': 'bag', 'কৌটা': 'can', 'জার': 'jar', 'রোল': 'roll', 'শিট': 'sheet', 'রিম': 'ream', 'টন': 'ton',
    litre: 'l', liter: 'l', piece: 'pcs', pieces: 'pcs', pc: 'pcs', metre: 'm', meter: 'm', strips: 'strip', tablets: 'tablet', bottles: 'bottle', pairs: 'pair',
  };
  return alias[s] ?? alias[low] ?? (Object.entries(UNIT_INFO) as Array<[Unit, UnitInfo]>).find(([, i]) => i.bn === s)?.[0] ?? null;
}

/** Ready-made pack sizes a category can add with one tap on the product form (unit conversion). */
export const UNIT_PRESETS: Partial<Record<Category, ReadonlyArray<{ unitName: Unit; factor: number; hint: [string, string] }>>> = {
  pharmacy: [
    { unitName: 'strip', factor: 10, hint: ['১ পাতা = ১০টি', '1 strip = 10'] },
    { unitName: 'box', factor: 100, hint: ['১ বক্স = ১০০টি', '1 box = 100'] },
  ],
  hardware: [
    { unitName: 'dozen', factor: 12, hint: ['১ ডজন = ১২', '1 dozen = 12'] },
    { unitName: 'box', factor: 50, hint: ['১ বক্স = ৫০', '1 box = 50'] },
  ],
};

/** Medicine dosage form (medicine database `form`) → the natural counting unit, used when a medicine is picked on the product form. */
export function unitForDosageForm(form: string | null | undefined): Unit | null {
  const f = (form ?? '').toLowerCase();
  if (!f) return null;
  if (/tab/.test(f)) return 'tablet';
  if (/cap/.test(f)) return 'capsule';
  if (/syrup|suspension|solution|drop|liquid|elixir|mixture/.test(f)) return 'bottle';
  if (/injection|infusion|vial/.test(f)) return 'vial';
  if (/ampoule|ampul/.test(f)) return 'ampoule';
  if (/cream|ointment|gel|paste/.test(f)) return 'tube';
  if (/sachet|powder|granule/.test(f)) return 'sachet';
  return null;
}
